import React, { useState, useEffect, useRef } from "react";
import { Button } from "../../Components/ui/button";
import { Input } from "../../Components/ui/input";
import { Badge } from "../../Components/ui/badge";
import { useNavigate } from "react-router-dom";
import {
  DotsHorizontalIcon,
  CaretSortIcon,
  PlayIcon,
  PauseIcon,
  StopwatchIcon,
  MagnifyingGlassIcon,
  CheckIcon,
} from "@radix-ui/react-icons";
import { format } from "date-fns";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../Components/ui/table";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../Components/ui/dropdown-menu";
import axios from "axios";
import { useToast } from "../../Components/ui/use-toast";
import { Toaster } from "../../Components/ui/toaster";
import TemplateDropdownMenuDemo from "../../Components/Filter/TemplateDropdown";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "src/Components/ui/dialog";
import { useDispatch } from "react-redux";
import { setCreateBreadCrumb } from "../../State/slices/AdvertiserAccountSlice";
import { useSelector } from "react-redux";
import { RootState } from "../../State/store";
import CircularProgress from "@mui/material/CircularProgress";

interface templateTable {
  template_id: string;
  template_name: string;
  channel_type: string;
  channel_id: number;
  status: string;
  last_updated: string;
  components: string;
  language: string;
  category: string;
  sub_category: string;
}

const TemplateList: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const toast = useToast();

  const workspaceId = useSelector(
    (state: RootState) => state.authentication.workspace_id
  );
  const userPermissions = useSelector(
    (state: RootState) => state.advertiserAccount.permissions
  );
  const apiUrlAdvAcc = useSelector((state: RootState) => state.authentication.apiURL);

  // States
  const [templateList, setTemplateList] = useState<templateTable[]>([]);
  const [hasTemplates, setHasTemplates] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterData, setFilterData] = useState({ filter: "None", value: "" });
  const [dateList, setDateList] = useState<string[]>([]);
  const [openMenuRowId, setOpenMenuRowId] = useState<number | null>(null);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [isApiExecuting, setIsApiExecuting] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<string | null>(null);
  const [templateNameToDelete, setTemplateNameToDelete] = useState<string>("");
  const [channel, setChannel] = useState<string>("");
  const [initialFetched, setInitialFetched] = useState(false);
  // Add this new state to track if we ever had templates
  const [everHadTemplates, setEverHadTemplates] = useState(false);

  // Sorting states
  const [sortField, setSortField] = useState<keyof templateTable | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // Checkbox selection
  const [checkboxSelectedRows, setCheckboxSelectedRows] = useState<string[]>([]);
  const [isAllSelected, setIsAllSelected] = useState(false);

  const debounceTimeoutRef = useRef<any>(null);

  // Calculate total pages
  const totalPages = Math.ceil(totalCount / rowsPerPage);

  const [initialLoad, setInitialLoad] = useState(true);

  // Single function to get templates with optional search
  const getTemplateList = async (pageNumber: number, pageSize: number, search?: string) => {
    if (!apiUrlAdvAcc || !workspaceId) return;

    setIsLoading(true);
    try {
      const searchParam = search?.trim() || null;

      const response = await axios.get(`${apiUrlAdvAcc}/GetTemplatesByWorkspaceId`, {
        params: {
          workspace_id: workspaceId,
          pageNumber,
          pageSize,
          searchTerm: searchParam,
        },
      });

      if (response.data.status === "Success" && response.data.templateDetails) {
        setTemplateList(response.data.templateDetails);
        setTotalCount(response.data.totalCount);
        setHasTemplates(response.data.templateDetails.length > 0);
        setDateList(response.data.lastEditedDates || []);
        setCheckboxSelectedRows([]);
        setIsAllSelected(false);
        
        // Track if we ever had templates (for the overall workspace)
        if (response.data.totalCount > 0) {
          setEverHadTemplates(true);
        }
      } else {
        setTemplateList([]);
        setTotalCount(0);
        setDateList([]);
        setHasTemplates(false);
      }
    } catch (error) {
      setTemplateList([]);
      setTotalCount(0);
      setHasTemplates(false);
      toast.toast({
        title: "Error",
        description: "Failed to fetch templates.",
      });
    } finally {
      setIsLoading(false);
      setInitialLoad(false);
    }
  };

  useEffect(() => {
    if (!apiUrlAdvAcc || !workspaceId) return;

    // Only run once on initial load
    if (!initialFetched && filterData.filter === "None") {
      setInitialFetched(true);
      getTemplateList(currentPage, rowsPerPage, searchTerm);
      return;
    }

    // Run on page or size change
    if (initialFetched && filterData.filter === "None") {
      getTemplateList(currentPage, rowsPerPage, searchTerm);
    }
  }, [apiUrlAdvAcc, workspaceId, currentPage, rowsPerPage, filterData]);

  useEffect(() => {
    if (filterData.filter !== "None" || !initialFetched) return;

    if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);

    debounceTimeoutRef.current = setTimeout(() => {
      setCurrentPage(1);
      getTemplateList(1, rowsPerPage, searchTerm);
    }, 500);

    return () => {
      if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
    };
  }, [searchTerm]);

  useEffect(() => {
    if (
      filterData.filter !== "None" &&
      filterData.value !== "" &&
      workspaceId &&
      apiUrlAdvAcc
    ) {
      fetchFilteredTemplates(filterData.filter, filterData.value);
    }
  }, [filterData, currentPage, rowsPerPage, apiUrlAdvAcc, workspaceId]);

  const fetchFilteredTemplates = async (criteria: string, subFilter: string) => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${apiUrlAdvAcc}/GetTemplatesWithFilter`, {
        params: {
          workspace_id: workspaceId,
          criteria: criteria,
          subFilter: subFilter,
          pageNumber: currentPage,
          pageSize: rowsPerPage,
        },
      });

      if (response.data.status === "Success") {
        setTemplateList(response.data.templateDetails);
        setTotalCount(response.data.filteredCount);
        // Track if we ever had templates
        if (response.data.filteredCount > 0) {
          setEverHadTemplates(true);
        }
      } else {
        setTemplateList([]);
        setTotalCount(0);
      }
    } catch (error) {
      toast.toast({
        title: "Error",
        description: "Failed to fetch filtered templates.",
      });
      setTemplateList([]);
      setTotalCount(0);
      setDateList([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Sorting logic - client side for demo (you may want to do server side)
  const sortedTemplates = React.useMemo(() => {
    if (!sortField) return templateList;
    const sorted = [...templateList].sort((a, b) => {
      const aField = a[sortField];
      const bField = b[sortField];
      if (aField == null) return 1;
      if (bField == null) return -1;

      if (typeof aField === "string" && typeof bField === "string") {
        return sortOrder === "asc"
          ? aField.localeCompare(bField)
          : bField.localeCompare(aField);
      }

      if (typeof aField === "number" && typeof bField === "number") {
        return sortOrder === "asc" ? aField - bField : bField - aField;
      }

      // Dates as string ISO format
      if (sortField === "last_updated") {
        const aDate = new Date(aField);
        const bDate = new Date(bField);
        return sortOrder === "asc"
          ? aDate.getTime() - bDate.getTime()
          : bDate.getTime() - aDate.getTime();
      }

      return 0;
    });

    return sorted;
  }, [templateList, sortField, sortOrder]);

  // Handle sort icon click
  const handleSort = (field: keyof templateTable) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  // Checkbox row selection
  const handleCheckboxRowSelect = (id: string) => {
    setCheckboxSelectedRows((prev) => {
      const isSelected = prev.includes(id);
      const newSelectedRows = isSelected
        ? prev.filter((rowId) => rowId !== id)
        : [...prev, id];

      setIsAllSelected(newSelectedRows.length === templateList.length);
      return newSelectedRows;
    });
  };

  const handleSelectAll = () => {
    if (isAllSelected) {
      setCheckboxSelectedRows([]);
    } else {
      const allIds = templateList.map((template) => template.template_id);
      setCheckboxSelectedRows(allIds);
    }
    setIsAllSelected(!isAllSelected);
  };

  // Pagination controls
  const handlePageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
  };

  // Delete handlers
  const handleDeleteClick = (templateId: string, templateChannel: string) => {
    setTemplateToDelete(templateId);
    const found = templateList.find((t) => t.template_id === templateId);
    setTemplateNameToDelete(found?.template_name || "");
    setChannel(templateChannel);
    setIsAlertOpen(true);
  };

  const confirmDelete = async () => {
    if (!templateToDelete) return;
    setIsApiExecuting(true);
    try {
      const payload = {
        id: templateToDelete,
        template_name: templateNameToDelete,
        workspace_id: workspaceId,
        channel_type: channel,
      };

      const response = await axios.delete(`${apiUrlAdvAcc}/DeleteMessageTemplate`, {
        data: payload,
      });

      if (response.status === 200) {
        toast.toast({
          title: "Success",
          description: "The Template was deleted successfully",
        });
        setIsAlertOpen(false);
        // Refresh current view
        getTemplateList(currentPage, rowsPerPage, searchTerm);
      } else {
        toast.toast({
          title: "Error",
          description: "Failed to delete the Template",
        });
      }
    } catch (e: any) {
      if (e.response && e.response.status === 500) {
        const statusDescription = e.response.data?.status_Description;
        if (statusDescription?.includes("REFERENCE constraint")) {
          toast.toast({
            title: "Warning",
            description: "Template is being used and cannot be deleted.",
          });
        } else {
          toast.toast({
            title: "Error",
            description: "Failed to delete the Template.",
          });
        }
      } else {
        toast.toast({
          title: "Error",
          description: "An unexpected error occurred while deleting the template.",
        });
      }
    } finally {
      setIsApiExecuting(false);
      setIsAlertOpen(false);
    }
  };

  // Status icon rendering
  const renderStatusIcon = (status: string) => {
    switch (status) {
      case "Live":
        return <PlayIcon className="text-gray-500" />;
      case "Pending":
        return <StopwatchIcon className="text-gray-500" />;
      case "Paused":
        return <PauseIcon className="text-gray-500" />;
      case "In review":
        return <MagnifyingGlassIcon className="text-gray-500" />;
      case "Completed":
        return <CheckIcon className="text-gray-500 rounded-full border border-gray-500" />;
      default:
        return null;
    }
  };

  const formatDateTime = (dateStr: string | null | undefined) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "N/A";
    return `${format(date, "dd-MM-yyyy")} ∙${format(date, "HH:mm")}`;
  };

  // Determine if we should show table or fallback UI
  const shouldShowTable = everHadTemplates || searchTerm.trim() !== "" || filterData.filter !== "None" || isLoading;
  const shouldShowFallback = !everHadTemplates && initialFetched && searchTerm.trim() === "" && filterData.filter === "None" && !isLoading;

  return (
    <div>
      <Toaster />
      {isLoading && initialLoad ? (
        <div className="flex flex-col items-center justify-center h-[500px]">
          <CircularProgress color="primary" />
        </div>
      ) : (
        <>
          {userPermissions.includes("ADV_Template_Create") && (
            <div className="fixed flex justify-end items-end right-0 top-[-15px] z-20 p-4 mr-2">
              <Button
                onClick={() => {
                  dispatch(setCreateBreadCrumb(true));
                  navigate("/navbar/CreateTemplate");
                }}
                className="w-36 text-sm font-thin h-[35px] mt-[10px]"
              >
                Create template
              </Button>
            </div>
          )}

          {shouldShowTable ? (
            <>
              <div className="flex mt-2">
                <div>
                  <Input
                    placeholder="Search template by name..."
                    className="w-[350px]"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <div className="flex items-end ml-auto">
                  <TemplateDropdownMenuDemo
                    setFilterData={setFilterData}
                    filterData={filterData}
                    dateList={dateList}
                  />
                </div>
              </div>

              <div className="rounded-md border mt-2">
                <div className="max-h-[60vh] overflow-y-auto">
                  <Table
                    className="rounded-xl whitespace-nowrap border-gray-200"
                    style={{ color: "#020202", fontSize: "15px" }}
                  >
                    <TableHeader className="text-center">
                      <TableRow className="sticky top-0 z-10 bg-white hover:bg-white">
                        <TableHead>
                          <div className="flex items-center gap-6 justify-start cursor-pointer">
                            <input
                              type="checkbox"
                              checked={isAllSelected}
                              onChange={handleSelectAll}
                              className={`text-muted-foreground ${isAllSelected
                                  ? "accent-gray-700 bg-grey-700 text-red-500"
                                  : ""
                                }`}
                            />
                            Template name{" "}
                            <CaretSortIcon
                              onClick={() => handleSort("template_name")}
                              className="cursor-pointer"
                            />
                          </div>
                        </TableHead>
                        <TableHead>
                          <div className="flex items-center gap-6 justify-start cursor-pointer">
                            Type{" "}
                            <CaretSortIcon
                              onClick={() => handleSort("channel_type")}
                              className="cursor-pointer"
                            />
                          </div>
                        </TableHead>
                        <TableHead>
                          <div className="flex items-center gap-6 cursor-pointer">
                            Status{" "}
                            <CaretSortIcon
                              onClick={() => handleSort("status")}
                              className="cursor-pointer"
                            />
                          </div>
                        </TableHead>
                        <TableHead>
                          <div className="flex items-center gap-6 cursor-pointer">
                            Last edited{" "}
                            <CaretSortIcon
                              onClick={() => handleSort("last_updated")}
                              className="cursor-pointer"
                            />
                          </div>
                        </TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>

                    <TableBody>
                      {isLoading && !initialLoad ? (
                        // Show "Loading..." text in table cell for pagination/search/filter loading
                        <TableRow>
                          <TableCell
                            colSpan={5}
                            className="text-center py-4 text-gray-500 font-medium"
                          >
                            Loading...
                          </TableCell>
                        </TableRow>
                      ) : sortedTemplates.length > 0 ? (
                        sortedTemplates.map((temp) => (
                          <TableRow key={temp.template_id}>
                            <TableCell>
                              <div className="flex items-center gap-6">
                                <input
                                  type="checkbox"
                                  checked={checkboxSelectedRows.includes(temp.template_id)}
                                  onChange={() => handleCheckboxRowSelect(temp.template_id)}
                                  className={`accent-gray-700 bg-grey-700 text-red-500 ${isAllSelected ? "accent-gray-700 bg-grey-700 text-red-500" : ""
                                    }`}
                                />
                                {temp.template_name}
                              </div>
                            </TableCell>
                            <TableCell className="text-left">
                              <Badge
                                style={
                                  temp.channel_type === "WhatsApp"
                                    ? { backgroundColor: "#479E98" }
                                    : temp.channel_type === "SMS"
                                      ? { backgroundColor: "#DFA548" }
                                      : temp.channel_type === "Push Notification" ||
                                        temp.channel_type === "Push"
                                        ? { backgroundColor: "#B87867" }
                                        : temp.channel_type === "RCS" ||
                                          temp.channel_type === "RCS messages"
                                          ? { backgroundColor: "#61177E" }
                                          : temp.channel_type === "Email"
                                            ? { backgroundColor: "#3B82F6" }
                                            : { backgroundColor: "#6B7280" }
                                }
                              >
                                {temp.channel_type}
                              </Badge>
                            </TableCell>
                            <TableCell className="py-4 flex items-center gap-2">
                              {renderStatusIcon(temp.status)} {temp.status}
                            </TableCell>
                            <TableCell className="text-left">
                              <span>{formatDateTime(temp.last_updated)}</span>
                            </TableCell>
                            <TableCell>
                              {userPermissions.includes("ADV_Template_Edit") && (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <DotsHorizontalIcon
                                      style={{ color: "#020617" }}
                                      className="cursor-pointer w-5 h-5"
                                    />
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="w-24 bg-white">
                                    <DropdownMenuItem
                                      className="cursor-pointer"
                                      onClick={() =>
                                        navigate("/navbar/CreateTemplate", {
                                          state: {
                                            templateId: temp.template_id,
                                            channelId: temp.channel_id,
                                          },
                                        })
                                      }
                                    >
                                      Edit
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      className="cursor-pointer"
                                      onClick={() =>
                                        handleDeleteClick(temp.template_id, temp.channel_type)
                                      }
                                    >
                                      Delete
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={5}
                            className="text-center py-4 text-gray-500 font-medium"
                          >
                            {searchTerm.trim() !== "" && filterData.filter === "None"
                              ? "No template found for the searched item."
                              : filterData.filter !== "None" && filterData.value !== ""
                                ? "No template found for the selected filter."
                                : "No templates available."}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Show pagination only when there are templates and not loading */}
              {sortedTemplates.length > 0 && !isLoading && (
                <div className="flex justify-between items-center mt-4">
                  <div className="text-gray-500 text-sm">
                    {`Showing ${(currentPage - 1) * rowsPerPage + 1} - ${Math.min(
                      currentPage * rowsPerPage,
                      totalCount
                    )} of ${totalCount} templates`}
                  </div>
                  <div className="flex items-center space-x-4 font-medium text-sm">
                    <span className="text-[#020617] font-medium text-[14px]">Rows per page</span>
                    <div className="relative inline-block ml-2">
                      <select
                        className="cursor-pointer border border-gray-300 rounded-md px-2 py-1 appearance-none focus:outline-none focus:ring-1 focus:ring-blue-500"
                        style={{
                          width: "60px",
                          height: "30px",
                          textAlign: "left",
                          fontSize: "12px",
                          fontWeight: "400",
                          borderColor: "#E5E7EB",
                          boxShadow: "inset 0 1px 2px rgba(0, 0, 0, 0.1)",
                        }}
                        value={rowsPerPage}
                        onChange={(e) => handleRowsPerPageChange(Number(e.target.value))}
                      >
                        {[5, 10, 20].map((num) => (
                          <option key={num} value={num}>
                            {num}
                          </option>
                        ))}
                      </select>
                      <CaretSortIcon className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none text-gray-500 w-4 h-4" />
                    </div>

                    <div className="ml-4 mr-4">
                      <span className="text-[#020617] text-[14px] font-medium">{`Page ${currentPage} of ${totalPages}`}</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        disabled={currentPage === 1}
                        className={`border p-1 pr-2 pl-2 rounded text-gray-500 ${currentPage === 1
                          ? "cursor-not-allowed bg-gray-100"
                          : "hover:bg-gray-200"
                          }`}
                        onClick={() => handlePageChange(1)}
                      >
                        «
                      </button>
                      <button
                        disabled={currentPage === 1}
                        className={`border p-1 pr-2 pl-2 rounded text-gray-500 ${currentPage === 1
                          ? "cursor-not-allowed bg-gray-100"
                          : "hover:bg-gray-200"
                          }`}
                        onClick={() => handlePageChange(currentPage - 1)}
                      >
                        ‹
                      </button>
                      <button
                        disabled={currentPage === totalPages}
                        className={`border p-1 pr-2 pl-2 rounded text-gray-500 ${currentPage === totalPages
                          ? "cursor-not-allowed bg-gray-100"
                          : "hover:bg-gray-200"
                          }`}
                        onClick={() => handlePageChange(currentPage + 1)}
                      >
                        ›
                      </button>
                      <button
                        disabled={currentPage === totalPages}
                        className={`border p-1 pr-2 pl-2 rounded text-gray-500 ${currentPage === totalPages
                          ? "cursor-not-allowed bg-gray-100"
                          : "hover:bg-gray-200"
                          }`}
                        onClick={() => handlePageChange(totalPages)}
                      >
                        »
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : shouldShowFallback ? (
            // Fallback UI when no templates exist and no search is active
            <div className="flex flex-col items-center justify-center h-[500px]">
              <h2 className="text-[24px] font-bold mb-1 text-[#000000]">
                Here you will see all your templates
              </h2>
              {userPermissions.includes("ADV_Template_Create") && (
                <div>
                  <p className="text-[#64748B] font-normal mb-1 text-[14px]">
                    Click the button below to create your first template.
                  </p>
                  <Button
                    onClick={() => navigate("/navbar/CreateTemplate")}
                    className="inline-flex items-center justify-center text-[14px] font-medium text-[#F8FAFC] px-5 py-5 whitespace-nowrap"
                    style={{ width: "auto", minWidth: "unset" }}
                  >
                    Create template
                  </Button>
                </div>
              )}
            </div>
          ) : null}
        </>
      )}
      <Dialog open={isAlertOpen} onOpenChange={setIsAlertOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-18px font-semibold text-[#09090B] mb-2">
              Delete Template
            </DialogTitle>
            <DialogDescription className="text-14px font-medium text-[#71717A]">
              Are you sure you want to delete this Template?
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-4">
            <Button
              disabled={isApiExecuting}
              variant="outline"
              className="px-4 py-2 w-24"
              onClick={() => setIsAlertOpen(false)}
            >
              Cancel
            </Button>
            <Button
              className="px-4 py-2 w-24 bg-[#EF4444]"
              onClick={confirmDelete}
              disabled={isApiExecuting}
            >
              {isApiExecuting ? "Deleting..." : "OK"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TemplateList;